/**
 * Maquetación y dibujo del certificado del ratón sobre un canvas.
 *
 * Mismo motivo que en `letter-canvas`: el padre se lleva una imagen que puede
 * guardar en el carrete, mandar por WhatsApp o imprimir, porque este tráfico
 * llega de móvil por la noche y `window.print()` a secas es lo que peor hace un
 * móvil. Lo que cambia es la forma: un diploma tiene marco, sello y firma, así
 * que la maqueta no es la de una carta y no se puede reutilizar la suya.
 *
 * `layoutCertificate` es pura y recibe la función de medida inyectada, así que
 * se testea sin canvas; `drawCertificate` es el único trozo que toca el
 * contexto 2D. El partido de líneas se reutiliza de `letter-canvas`.
 */

import { wrapLines, type Measure } from './letter-canvas'
import type { ToothCertificate } from './tooth-certificate'

export type CertificateAlign = 'left' | 'center' | 'right'

export type CertificateBlock = {
  text: string
  font: string
  x: number
  y: number
  align: CertificateAlign
  /** Color propio del bloque. Sin él se pinta con la tinta base. */
  color?: string
}

export type CertificateLayout = {
  width: number
  height: number
  blocks: CertificateBlock[]
  /** Sello circular: centro y radio. */
  seal: { x: number; y: number; radius: number }
  /** Línea sobre la que "firma" el ratón. */
  signatureRule: { x: number; y: number; width: number }
}

/** Lienzo apaisado, como un diploma, pensado para imprimir en A4 horizontal. */
export const CERTIFICATE_CANVAS = {
  width: 1200,
  /** Alto mínimo: por debajo el diploma parece un recibo, no un diploma. */
  minHeight: 860,
  padding: 100,
  /** Marco doble: el grueso y, a `frameGap` por dentro, el fino. */
  frameInset: 44,
  frameGap: 14,
  authorityFont: 'bold 26px Georgia, "Times New Roman", serif',
  titleFont: 'bold 62px Georgia, "Times New Roman", serif',
  awardedToFont: 'italic 28px Georgia, "Times New Roman", serif',
  nameFont: 'bold 74px Georgia, "Times New Roman", serif',
  citationFont: '30px Georgia, "Times New Roman", serif',
  detailFont: '26px Georgia, "Times New Roman", serif',
  signatureFont: 'italic 42px Georgia, "Times New Roman", serif',
  roleFont: '22px Georgia, "Times New Roman", serif',
  sealFont: 'bold 20px Georgia, "Times New Roman", serif',
  lineHeight: 44,
  citationLineHeight: 44,
  detailLineHeight: 40,
  paper: '#fdf8ec',
  ink: '#3f3222',
  /** Dorado del marco, el sello y los rótulos: lo que hace que parezca oficial. */
  accent: '#a9752c',
  sealRadius: 82,
} as const

/** Alto que el pie (firma y sello) necesita bajo el último bloque de contenido. */
const FOOTER_HEIGHT = 220

function detailLine(label: string, value: string): string {
  return `${label}: ${value}`
}

/**
 * Coloca el contenido centrado en columna y ancla el pie al borde inferior.
 *
 * Dos pasadas a propósito: el alto final no se conoce hasta terminar el
 * contenido (una explicación larga del padre añade líneas), y la firma y el
 * sello se miden desde abajo para que el diploma no quede descuadrado.
 */
export function layoutCertificate(
  certificate: ToothCertificate,
  measure: Measure
): CertificateLayout {
  const canvas = CERTIFICATE_CANVAS
  const { width, padding } = canvas
  const maxWidth = width - padding * 2
  const center = width / 2
  const blocks: CertificateBlock[] = []
  let y = padding + 40

  function pushWrapped(
    text: string,
    font: string,
    lineHeight: number,
    color?: string,
    limit = maxWidth
  ) {
    for (const line of wrapLines(text, font, limit, measure)) {
      blocks.push({ text: line, font, x: center, y, align: 'center', color })
      y += lineHeight
    }
  }

  pushWrapped(certificate.authority.toUpperCase(), canvas.authorityFont, 64, canvas.accent)
  pushWrapped(certificate.title, canvas.titleFont, 84)
  pushWrapped(certificate.awardedTo, canvas.awardedToFont, 68)
  pushWrapped(certificate.childName, canvas.nameFont, 96, canvas.accent)
  pushWrapped(certificate.citation, canvas.citationFont, canvas.citationLineHeight)
  y += 20
  for (const detail of certificate.details) {
    pushWrapped(detailLine(detail.label, detail.value), canvas.detailFont, canvas.detailLineHeight)
  }

  const height = Math.max(canvas.minHeight, y + FOOTER_HEIGHT)

  // Pie anclado abajo: sello a la izquierda, firma a la derecha.
  const seal = {
    x: padding + canvas.sealRadius + 10,
    y: height - padding - canvas.sealRadius + 10,
    radius: canvas.sealRadius,
  }
  const signatureX = width - padding - 200
  const signatureBaseline = height - padding - 40
  blocks.push({
    text: certificate.signature,
    font: canvas.signatureFont,
    x: signatureX,
    y: signatureBaseline - 60,
    align: 'center',
  })
  blocks.push({
    text: certificate.signatureRole,
    font: canvas.roleFont,
    x: signatureX,
    y: signatureBaseline,
    align: 'center',
    color: canvas.accent,
  })

  return {
    width,
    height,
    blocks,
    seal,
    signatureRule: { x: signatureX, y: signatureBaseline - 34, width: 380 },
  }
}

function drawFrame(context: CanvasRenderingContext2D, layout: CertificateLayout): void {
  const { frameInset, frameGap, accent } = CERTIFICATE_CANVAS
  context.strokeStyle = accent
  context.lineWidth = 6
  context.strokeRect(
    frameInset,
    frameInset,
    layout.width - frameInset * 2,
    layout.height - frameInset * 2
  )
  context.lineWidth = 2
  context.strokeRect(
    frameInset + frameGap,
    frameInset + frameGap,
    layout.width - (frameInset + frameGap) * 2,
    layout.height - (frameInset + frameGap) * 2
  )
}

function drawSeal(
  context: CanvasRenderingContext2D,
  layout: CertificateLayout,
  lines: string[]
): void {
  const { accent, sealFont } = CERTIFICATE_CANVAS
  const { x, y, radius } = layout.seal

  context.strokeStyle = accent
  context.lineWidth = 4
  context.beginPath()
  context.arc(x, y, radius, 0, Math.PI * 2)
  context.stroke()
  context.lineWidth = 1.5
  context.beginPath()
  context.arc(x, y, radius - 10, 0, Math.PI * 2)
  context.stroke()

  context.font = sealFont
  context.fillStyle = accent
  context.textAlign = 'center'
  const lineHeight = 26
  const firstLineY = y - ((lines.length - 1) * lineHeight) / 2 + 8
  lines.forEach((line, index) => {
    context.fillText(line, x, firstLineY + index * lineHeight)
  })
}

/**
 * Pinta el certificado en el canvas y lo deja dimensionado al contenido.
 *
 * `scale` sube la resolución del bitmap sin cambiar la maqueta, para que el
 * diploma se vea nítido en pantallas densas y, sobre todo, al imprimirlo.
 */
export function drawCertificate(
  canvas: HTMLCanvasElement,
  certificate: ToothCertificate,
  scale = 2
): CertificateLayout {
  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas 2d context unavailable')

  const measure: Measure = (text, font) => {
    context.font = font
    return context.measureText(text).width
  }
  const layout = layoutCertificate(certificate, measure)

  canvas.width = layout.width * scale
  canvas.height = layout.height * scale
  context.setTransform(scale, 0, 0, scale, 0, 0)

  context.fillStyle = CERTIFICATE_CANVAS.paper
  context.fillRect(0, 0, layout.width, layout.height)
  drawFrame(context, layout)

  context.textBaseline = 'alphabetic'
  for (const block of layout.blocks) {
    context.font = block.font
    context.fillStyle = block.color ?? CERTIFICATE_CANVAS.ink
    context.textAlign = block.align
    context.fillText(block.text, block.x, block.y)
  }

  context.strokeStyle = CERTIFICATE_CANVAS.ink
  context.lineWidth = 1.5
  context.beginPath()
  context.moveTo(layout.signatureRule.x - layout.signatureRule.width / 2, layout.signatureRule.y)
  context.lineTo(layout.signatureRule.x + layout.signatureRule.width / 2, layout.signatureRule.y)
  context.stroke()

  drawSeal(context, layout, certificate.seal)

  return layout
}
